/** Plain geometry on a city's scale, with no dependencies: zones (zones.ts) and sharing stops out (share.ts) use it. */

export interface Spot {
  lat: number;
  lng: number;
}

/**
 * Whether [p] is inside the zone's outline (even-odd: the edge counts as inside). Lat and lng are taken as flat, which is
 * true enough across a city.
 */
export function inside(p: Spot, path: Spot[]): boolean {
  let hit = false;
  for (let i = 0, j = path.length - 1; i < path.length; j = i++) {
    const a = path[i];
    const b = path[j];
    if (a.lat > p.lat !== b.lat > p.lat) {
      const x = a.lng + ((p.lat - a.lat) / (b.lat - a.lat)) * (b.lng - a.lng);
      if (p.lng < x) hit = !hit;
      else if (p.lng === x) return true;
    }
  }
  return hit;
}

/** About how big a zone is, in km² (shoelace on a local flat projection): to say "about 4 km²". */
export function areaKm2(path: Spot[]): number {
  if (path.length < 3) return 0;
  const lat0 = path.reduce((a, p) => a + p.lat, 0) / path.length;
  const kx = 111.32 * Math.cos((lat0 * Math.PI) / 180);
  const ky = 110.57;
  let s = 0;
  for (let i = 0, j = path.length - 1; i < path.length; j = i++) s += path[j].lng * kx * (path[i].lat * ky) - path[i].lng * kx * (path[j].lat * ky);
  return Math.abs(s) / 2;
}

/** The middle of a zone's corners: where its name sits on the map. */
export const middleOf = (path: Spot[]): Spot => ({ lat: path.reduce((a, p) => a + p.lat, 0) / path.length, lng: path.reduce((a, p) => a + p.lng, 0) / path.length });
